import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAliveApps } from "../../../../api/commonApi";
import { useConnectedAppsStore } from "../../../stores/useConnectedAppsStore";
import { useUserStore } from "../../../stores/useUserStore";

export const useConnectedAppStatus = () => {
  const selectedUser = useUserStore((state) => state.selectedUser);
  const {
    setDesktopAgentStatus,
    setChromeExtensionStatus,
    setEdgeExtensionStatus,
  } = useConnectedAppsStore();
  const [hasCheckedHeartbeatOnce, setHasCheckedHeartbeatOnce] = useState(false);
  const hasCheckedHeartbeatOnceRef = useRef(hasCheckedHeartbeatOnce);
  const selectedUserRef = useRef(selectedUser);
  const aliveAppsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateAliveApps = useCallback(async () => {
    try {
      const user = selectedUserRef.current;
      const aliveApps = await fetchAliveApps(
        user && user.id ? `?user_id=${user.id}` : "",
      );

      setDesktopAgentStatus(aliveApps?.desktop_agent);
      setChromeExtensionStatus(aliveApps?.chromiumapp);
      setEdgeExtensionStatus(aliveApps?.edgeapp);
    } catch (e) {
      console.error("fetchAliveApps failed:", e);
    }
  }, [setChromeExtensionStatus, setDesktopAgentStatus, setEdgeExtensionStatus]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    if (!hasCheckedHeartbeatOnceRef.current) return;
    updateAliveApps();
  }, [selectedUser, updateAliveApps]);

  useEffect(() => {
    hasCheckedHeartbeatOnceRef.current = hasCheckedHeartbeatOnce;
    if (!hasCheckedHeartbeatOnce) return;
    if (aliveAppsTimerRef.current) return;

    aliveAppsTimerRef.current = setInterval(
      () => {
        updateAliveApps();
      },
      3 * 60 * 1000,
    );

    return () => {
      if (aliveAppsTimerRef.current) {
        clearInterval(aliveAppsTimerRef.current);
        aliveAppsTimerRef.current = null;
      }
    };
  }, [hasCheckedHeartbeatOnce, updateAliveApps]);

  return {
    hasCheckedHeartbeatOnce,
    hasCheckedHeartbeatOnceRef,
    setHasCheckedHeartbeatOnce,
    updateAliveApps,
  };
};
